import { useState } from "react";

const stepsA = [
  {
    n: "01",
    title: "拿到带水印的视频 / 链接",
    text: "在短视频或直播页点分享，把带水印的原链接或本地文件准备好。",
  },
  {
    n: "02",
    title: "粘贴进吃瓜神器解析框",
    text: "把链接粘进解析框，或直接上传本地视频，一键识别来源与画质档位。",
  },
  {
    n: "03",
    title: "选原画档去除水印",
    text: "确认选择原画档去水印，画质损失更小，细节更经得起二次回放。",
  },
  {
    n: "04",
    title: "保存到本地再归档",
    text: "下载到本地并归入对应合集，配合倍速与录屏即可随时回放复盘。",
  },
];

const guideTabs = [
  {
    key: "speed",
    icon: "ri-time-line",
    label: "倍速播放",
    lead: "追剧看直播常嫌节奏慢，倍速才是吃瓜神器的主力玩法。",
    points: [
      { k: "自由调速", v: "0.5x – 4x 无级切换，聊天灌水片段一键 2x 快进。" },
      { k: "片段循环", v: "单句台词 / 名场面反复循环，二创卡点不卡手。" },
      { k: "跳过静默", v: "自动识别冷场，压缩等待，只看有内容的段落。" },
    ],
    tip: "听不清的字幕，先降速到 0.75x 再看，比反复拉进度条更省心。",
  },
  {
    key: "record",
    icon: "ri-record-circle-line",
    label: "录屏回放",
    lead: "直播不舍得错过？录屏跟着直播走，回放随时补课。",
    points: [
      { k: "直播跟随", v: "边播边录，断流自动续录，长时录制不中断。" },
      { k: "自动分段", v: "按时间点切成多段文件，文件名自动带时间，回找超快。" },
      { k: "后台录制", v: "切走界面也继续录，边吃瓜边回放两不误。" },
    ],
    tip: "录屏前先在系统里关掉无关通知，避免弹窗声音混进成片。",
  },
  {
    key: "danmaku",
    icon: "ri-chat-3-line",
    label: "弹幕聚合",
    lead: "一个屏幕装不下所有讨论？把弹幕拉齐到同一时间轴。",
    points: [
      { k: "双端对齐", v: "两个平台的弹幕同屏同步滚动，氛围感直接翻倍。" },
      { k: "关键词捕捉", v: "高能名场面自动加标记，弹幕高潮一眼定位。" },
      { k: "弹幕回看", v: "回放视频也能叠上当时的弹幕，把热闹留住。" },
    ],
    tip: "对齐前先确认两边的起点一致，差几秒都会让名场面错位。",
  },
];

export default function GuideSection() {
  const [active, setActive] = useState(guideTabs[0].key);
  const current = guideTabs.find((t) => t.key === active) ?? guideTabs[0];

  return (
    <section id="guide" className="border-y border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <i className="ri-route-line" />
            玩法操作指南
          </span>
          <h2 className="mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
            从拿到素材到吃透全场，一步步来
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            去水印 / 解析先看下面的操作流程，倍速、录屏、弹幕聚合换个标签即切，覆盖追剧、直播、二创三条常见路径。
          </p>
        </div>

        {/* A. 去水印 / 解析 步骤流 */}
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3 rounded-3xl border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-bold text-foreground-950">
                <span className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-drop-line" />
                </span>
                去水印 / 解析标准流程
              </h3>
              <span className="hidden whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-800 sm:inline">
                约 30 秒上手
              </span>
            </div>
            <div className="mt-5 flex flex-col gap-3">
              {stepsA.map((s, i) => (
                <div key={s.n} className="relative flex items-start gap-4">
                  {i < stepsA.length - 1 && (
                    <span className="absolute left-[17px] top-9 h-[calc(100%-8px)] w-px bg-secondary-200" />
                  )}
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-secondary-300 bg-secondary-100 font-heading text-sm font-black text-foreground-800">
                    {s.n}
                  </span>
                  <div className="min-w-0 pt-1">
                    <h4 className="text-sm font-bold text-foreground-950">{s.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* B. 倍速 / 录屏 / 弹幕 标签面板 */}
          <div className="lg:col-span-2 flex flex-col rounded-3xl border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-center justify-between gap-2 rounded-full bg-secondary-100 p-1">
              {guideTabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1.5 text-xs transition-colors ${
                    active === t.key
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <i className={t.icon} />
                  <span className="hidden sm:inline">{t.label}</span>
                  <span className="sm:hidden">{t.label.slice(0, 2)}</span>
                </button>
              ))}
            </div>

            <div key={current.key} className="animate-pop mt-5 flex flex-1 flex-col">
              <h3 className="text-base font-bold text-foreground-950">
                <i className={`${current.icon} mr-2 text-accent-600`} />
                {current.label}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                {current.lead}
              </p>
              <ul className="mt-4 flex flex-col gap-2.5">
                {current.points.map((p) => (
                  <li
                    key={p.k}
                    className="rounded-xl border border-background-200 bg-background-50 px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <i className="ri-check-line text-accent-600" />
                      <span className="whitespace-nowrap text-sm font-bold text-foreground-950">
                        {p.k}
                      </span>
                    </div>
                    <p className="mt-1 pl-5 text-sm leading-relaxed text-foreground-600">
                      {p.v}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="mt-auto flex items-start gap-2 rounded-xl bg-secondary-100/70 px-3 py-2.5 pt-3">
                <i className="ri-lightbulb-line mt-0.5 shrink-0 text-secondary-700" />
                <p className="text-xs leading-relaxed text-secondary-900">
                  <span className="font-bold">小贴士：</span>
                  {current.tip}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}